import { editableRange, ensureEditableSelection } from './editableSelection';
import InlineImageControls from './InlineImageControls';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Jodit } from 'jodit';
import 'jodit/build/jodit.min.css';

// Keep the editable sheet in its original DOM so pagination can move its real nodes.
export default function FloatingSheetToolbar({ editorConfig, onColumnsChange, columns = {} }) {
    const host = useRef(null);
    const current = useRef(null);
    const config = useRef(editorConfig);
    const callback = useRef(onColumnsChange);
    const [target, setTarget] = useState(null);
    const [position, setPosition] = useState({ top: 90, left: 12, width: 777 });
    config.current = editorConfig;
    callback.current = onColumnsChange;
    useEffect(() => {
        let mounted = true;
        const close = (render = true) => {
            const active = current.current;
            current.current = null;
            if (active) {
                ensureEditableSelection(active.target, active.range);
                active.editor.e.fire('synchro');
                active.target.removeAttribute('data-jodit-active');
                active.editor.e.off(active.target);
                // Jodit's source is disposable; the React-owned editable sheet must remain mounted.
                active.editor.currentPlace.editor = document.createElement('div');
                active.editor.destruct();
                active.holder.remove();
            }
            if (render) setTarget(null);
        };
        const place = element => {
            const sheet = element.closest('.diagramacao-cover-canvas, .answer-sheet, .diagramacao-question-frame') || element;
            const rect = sheet.getBoundingClientRect();
            const width = Math.min(777, window.innerWidth - 24);
            setPosition({ top: Math.max(88, rect.top - (host.current?.offsetHeight || 90) - 8), left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), width });
        };
        const focus = event => {
            const element = event.target.closest?.('[data-inline-editor], [id^="page_content_"]');
            if (!element || !element.closest('#pdf_diagramado_2')) return;
            if (current.current?.target === element) { ensureEditableSelection(element, current.current.range); place(element); return; }
            requestAnimationFrame(() => {
                if (!mounted || !element.isConnected || !element.contains(document.activeElement)) return;
                if (current.current?.target === element) { ensureEditableSelection(element, current.current.range); place(element); return; }
                const range = editableRange(element);
                close();
                ensureEditableSelection(element, range, true);
                const holder = document.createElement('div');
                holder.hidden = true;
                const source = document.createElement('textarea');
                source.value = element.innerHTML;
                holder.append(source);
                document.body.append(holder);
                element.setAttribute('data-jodit-active', 'true');
                const disabledPlugins = Array.isArray(config.current?.disablePlugins)
                    ? config.current.disablePlugins : String(config.current?.disablePlugins || '').split(/[\s,]+/).filter(Boolean);
                const editor = Jodit.make(source, { ...config.current, readonly: false, inline: false,
                    // Rnd owns block resizing. Jodit's image overlay can recurse on fractional drag positions.
                    disablePlugins: [...disabledPlugins, 'resizer'],
                    toolbar: host.current.querySelector('.diagramacao-jodit-host'), toolbarSticky: false, toolbarInline: false,
                    toolbarAdaptive: true, toolbarButtonSize: 'small', height: 'auto', minHeight: 0,
                    statusbar: false, showCharsCounter: false, showWordsCounter: false, showXPathInStatusbar: false,
                    buttons: ['bold', 'italic', 'underline', '|', 'font', 'fontsize', 'brush', 'paragraph', 'ul', 'ol', 'align', '|', 'image', 'table', 'hr', 'eraser'],
                    events: {
                        createEditor: instance => { instance.editor = element; ensureEditableSelection(element, range); return false; },
                        beforeCommand: () => { if (!ensureEditableSelection(element, current.current?.range || range)) return false; },
                        beforeInsertNode: () => { ensureEditableSelection(element, current.current?.range || range); }
                    }
                });
                current.current = { target: element, editor, holder, range };
                editor.e.on('change.diagramacaoInline', () => {
                    if (element.isConnected) element.dispatchEvent(new Event('input', { bubbles: true }));
                });
                current.current.range = ensureEditableSelection(element, range, true);
                setTarget(element);
                place(element);
                requestAnimationFrame(() => { if (element.isConnected) place(element); });
            });
        };
        const outside = event => {
            if (host.current?.contains(event.target) || event.target.closest?.('.jodit-popup, .jodit-dialog, .modal, .diagramacao-image-controls, [data-inline-editor], [id^="page_content_"]')) return;
            close();
        };
        const scroll = () => { if (current.current?.target.isConnected) place(current.current.target); };
        const rememberSelection = () => {
            const active = current.current;
            const range = active && editableRange(active.target);
            if (range) active.range = range;
        };
        const guardSelection = event => {
            const active = current.current;
            if (active?.target.contains(event.target)) active.range = ensureEditableSelection(active.target, active.range);
        };
        document.addEventListener('selectionchange', rememberSelection);
        document.addEventListener('keydown', guardSelection, true);
        document.addEventListener('focusin', focus, true);
        document.addEventListener('mousedown', outside);
        window.addEventListener('scroll', scroll, true);
        window.addEventListener('resize', scroll);
        return () => { mounted = false; document.removeEventListener('selectionchange', rememberSelection); document.removeEventListener('keydown', guardSelection, true); document.removeEventListener('focusin', focus, true); document.removeEventListener('mousedown', outside); window.removeEventListener('scroll', scroll, true); window.removeEventListener('resize', scroll); close(false); };
    }, []);
    const page = target?.id?.startsWith('page_content_') ? Number(target.id.slice('page_content_'.length)) : null;
    return <><InlineImageControls />{createPortal(<div ref={host} className="diagramacao-floating-toolbar hidethis" style={{ ...position, display: target ? 'block' : 'none' }}>
        {page !== null && <label className="diagramacao-inline-columns">Colunas nesta página <select aria-label="Colunas nesta página" value={columns[page] || columns.default || 2} onChange={event => callback.current?.(page, Number(event.target.value))}><option value={1}>1 coluna</option><option value={2}>2 colunas</option></select></label>}
        <div className="diagramacao-jodit-host" />
    </div>, document.body)}</>;
}
